import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle, Eye, Move } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
}

interface ImageUploadProps {
  images: ImageFile[];
  onImagesChange: (images: ImageFile[]) => void;
  maxImages?: number;
  maxSizeInMB?: number;
  acceptedTypes?: string[];
  className?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ 
  images, 
  onImagesChange, 
  maxImages = 5,
  maxSizeInMB = 10,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp'],
  className 
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = maxSizeInMB * 1024 * 1024; // Convert MB to bytes

  // Validation function
  const validateFile = (file: File): string | null => {
    if (!acceptedTypes.includes(file.type)) {
      return `Bestandstype ${file.type} wordt niet ondersteund. Gebruik JPG, PNG of WebP.`;
    }

    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > maxSizeInMB) {
      return `Bestand te groot (${sizeInMB.toFixed(1)}MB). Maximum ${maxSizeInMB}MB toegestaan.`;
    }

    return null;
  };

  // Create preview from file
  const createPreview = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Compress image if needed
  const compressImage = (file: File, maxWidth: number = 800, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file); // Fallback to original
          }
        }, file.type, quality);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Process uploaded files
  const processFiles = async (files: FileList) => {
    const validFiles: ImageFile[] = [];
    const errors: string[] = [];

    setIsUploading(true);
    const progressMap: { [key: string]: number } = {};

    for (const file of Array.from(files)) {
      const fileId = crypto.randomUUID();
      progressMap[fileId] = 0;
      setUploadProgress({ ...progressMap });

      // Check if we've reached max images
      if (images.length + validFiles.length >= maxImages) {
        errors.push(`Maximum ${maxImages} foto's toegestaan`);
        break;
      }

      // File validation
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
        continue;
      }

      try {
        // Update progress - validation complete
        progressMap[fileId] = 30;
        setUploadProgress({ ...progressMap });

        // Compress image if it's larger than 1MB
        const finalFile = file.size > 1024 * 1024 ? await compressImage(file) : file;

        // Update progress - compression complete
        progressMap[fileId] = 70;
        setUploadProgress({ ...progressMap });

        // Create preview
        const preview = await createPreview(finalFile);

        // Update progress - complete
        progressMap[fileId] = 100;
        setUploadProgress({ ...progressMap });

        validFiles.push({
          id: fileId,
          file: finalFile,
          preview,
          name: file.name,
          size: finalFile.size
        });
      } catch (error) {
        errors.push(`Fout bij verwerken van ${file.name}`);
        console.error('Error processing file:', error);
      }
    }

    // Show validation errors
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
    }

    // Add valid files
    if (validFiles.length > 0) {
      onImagesChange([...images, ...validFiles]);
      toast.success(`${validFiles.length} foto${validFiles.length === 1 ? '' : '\'s'} succesvol toegevoegd!`);
    }

    setIsUploading(false);
    setUploadProgress({});
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  };

  // File input handler
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Remove image
  const removeImage = (id: string) => {
    const updatedImages = images.filter(image => image.id !== id);
    onImagesChange(updatedImages);
    toast.success('Foto verwijderd');
  };

  // Image reordering handlers
  const handleImageDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      const newImages = [...images];
      const draggedImage = newImages[draggedIndex];
      newImages.splice(draggedIndex, 1);
      newImages.splice(index, 0, draggedImage);
      onImagesChange(newImages);
      setDraggedIndex(index);
    }
  };

  const handleImageDragEnd = () => {
    setDraggedIndex(null);
  };

  // Preview handlers
  const openPreview = (preview: string) => {
    setPreviewImage(preview);
  };

  const clearAllImages = () => {
    onImagesChange([]);
    toast.success('Alle foto\'s verwijderd');
  };

  // Utility functions
  const getTotalSize = () => {
    return images.reduce((total, image) => total + image.size, 0);
  };

  const getOptimizedCount = () => {
    return images.filter(image => image.file.size < image.size).length;
  };
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Progress */}
      {isUploading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <p className="text-sm font-medium text-blue-800">
              Foto's verwerken...
            </p>
          </div>
          <div className="space-y-2">
            {Object.entries(uploadProgress).map(([fileId, progress]) => (
              <div key={fileId} className="bg-blue-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
          ${isDragOver 
            ? 'border-stone-400 bg-stone-50' 
            : 'border-stone-300 hover:border-stone-400 hover:bg-stone-25'
          }
          ${isUploading ? 'opacity-50 pointer-events-none' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />

        {isUploading ? (
          <div className="space-y-3">
            <div className="h-12 w-12 mx-auto border-2 border-stone-300 border-r-stone-600 rounded-full animate-spin" />
            <p className="text-stone-600">Foto's uploaden...</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Upload className="h-12 w-12 text-stone-400 mx-auto" />
            <div>
              <p className="text-stone-600 mb-1">
                Sleep je foto's hier of{' '}
                <button 
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-stone-600 font-semibold hover:text-stone-700 underline"
                >
                  klik om te uploaden
                </button>
              </p>
              <p className="text-sm text-stone-500">
                PNG, JPG, WebP tot {maxSizeInMB}MB • Maximum {maxImages} foto's
              </p>
              <p className="text-xs text-stone-400 mt-1">
                {images.length}/{maxImages} foto's toegevoegd
              </p>
            </div>
            {images.length < maxImages && (
              <Button 
                type="button" 
                variant="outline" 
                className="border-stone-300 text-stone-600 hover:bg-stone-50"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Selecteer Foto's
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-semibold text-stone-800">
                Geüploade foto's ({images.length}/{maxImages})
              </h4>
              <div className="flex items-center space-x-4 mt-1">
                <p className="text-sm text-stone-600">
                  Totale grootte: {formatFileSize(getTotalSize())}
                </p>
                {getOptimizedCount() > 0 && (
                  <div className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                    {getOptimizedCount()} geoptimaliseerd
                  </div>
                )}
              </div>
            </div>
            {images.length > 1 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearAllImages}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Alles verwijderen
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((image, index) => (
              <div 
                key={image.id}
                draggable
                onDragStart={(e) => handleImageDragStart(e, index)}
                onDragOver={(e) => handleImageDragOver(e, index)}
                onDragEnd={handleImageDragEnd}
                className={`relative bg-white rounded-xl border-2 ${
                  draggedIndex === index 
                    ? 'border-stone-400 shadow-lg scale-105' 
                    : 'border-stone-200'
                } overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200 group cursor-move`}
              >
                {/* Main Image Badge */}
                {index === 0 && (
                  <div className="absolute top-2 left-2 z-10">
                    <div className="bg-stone-600 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
                      Hoofdfoto
                    </div>
                  </div>
                )}

                {/* Drag Handle */}
                <div className="absolute top-2 right-12 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="bg-white bg-opacity-90 p-1 rounded-full shadow-sm">
                    <Move className="h-4 w-4 text-stone-600" />
                  </div>
                </div>
                {/* Image Preview */}
                <div className="aspect-square bg-gradient-to-br from-stone-50 to-stone-100 overflow-hidden relative">
                  <img
                    src={image.preview}
                    alt={image.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* Hover Overlay with Actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => openPreview(image.preview)}
                        className="bg-white hover:bg-gray-100 shadow-lg"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={() => removeImage(image.id)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-md opacity-0 group-hover:opacity-100 duration-200"
                  title="Verwijder foto"
                >
                  <X className="h-4 w-4" />
                </button>

                {/* File Info */}
                <div className="p-3 bg-white border-t-2 border-stone-100">
                  <p className="text-sm font-medium text-stone-800 truncate mb-1" title={image.name}>
                    {image.name}
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-stone-500">
                      {formatFileSize(image.size)}
                    </p>
                    {image.file.size !== image.size && (
                      <div className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                        Geoptimaliseerd
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Reorder Instructions */}
          {images.length > 1 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Move className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800 mb-1">
                    Sleep foto's om te herordenen
                  </p>
                  <p className="text-xs text-blue-600">
                    De eerste foto wordt gebruikt als hoofdfoto in je advertentie
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload Guidelines */}
      {images.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-blue-900 mb-1">Tips voor goede foto's:</p>
              <ul className="text-blue-700 space-y-1">
                <li>• Gebruik heldere, scherpe foto's</li>
                <li>• Toon je werk, proces of eindresultaat</li>
                <li>• Goede verlichting maakt het verschil</li>
                <li>• Voeg variatie toe met verschillende hoeken</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Preview Modal */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl w-full max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="text-lg font-semibold text-stone-800">
              Foto preview
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 pt-2">
            {previewImage && (
              <div className="flex items-center justify-center bg-gray-50 rounded-lg overflow-hidden">
                <img
                  src={previewImage}
                  alt="Preview"
                  className="max-w-full max-h-[70vh] object-contain"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageUpload;